import { ToggleCrossSellsUpdatedEvent } from '@theme/events';

/**
 * Gravure personnalisée (blocks/cf-gravure.liquid).
 *
 * Le formulaire produit natif (assets/product-form.js) ajoute au panier les articles
 * renvoyés par `productForm.toggleCrossSells.getSelectedItems()` et les inclut dans
 * le prix total. On enveloppe cette source pour y ajouter la gravure, en conservant
 * les cross-sells à cocher natifs éventuels.
 */
class CfGravure extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-ref="toggle"]');
    this.panel = this.querySelector('[data-ref="panel"]');
    this.input = this.querySelector('[data-ref="input"]');
    this.counter = this.querySelector('[data-ref="counter"]');
    this.error = this.querySelector('[data-ref="error"]');
    this.plaque = this.querySelector('[data-ref="plaque"]');
    this.emptyLabel = this.plaque.textContent;
    this.price = parseInt(this.dataset.price, 10) || 0;

    this.toggle.addEventListener('change', () => this.onToggle());
    this.input.addEventListener('input', () => this.onInput());
    this.updatePlaque();

    const section = this.closest('.shopify-section') || document;
    customElements.whenDefined('product-form').then(() => {
      const productForm = section.querySelector('product-form');
      if (productForm) this.attach(productForm);
    });
  }

  get isActive() {
    return this.toggle.checked;
  }

  attach(productForm) {
    const native = productForm.toggleCrossSells;
    productForm.toggleCrossSells = {
      getSelectedItems: () => {
        const base = native?.getSelectedItems?.() || { items: [], total_price: 0, total_compare_at_price: 0 };
        if (!this.isActive) return base;
        return {
          items: [...base.items, this.item()],
          total_price: base.total_price + this.price,
          total_compare_at_price: base.total_compare_at_price + this.price,
        };
      },
    };

    // Bloque l'ajout au panier si la gravure est activée sans texte
    // (capture sur <product-form> : s'exécute avant le gestionnaire du <form>)
    productForm.addEventListener(
      'submit',
      (event) => {
        if (!this.isActive || this.text() !== '') return;
        event.preventDefault();
        event.stopPropagation();
        this.showError();
      },
      true,
    );
  }

  text() {
    return this.input.value.trim();
  }

  item() {
    return {
      id: this.dataset.variantId,
      quantity: 1,
      properties: { [this.dataset.property || 'Texte gravé']: this.text() },
    };
  }

  onToggle() {
    this.panel.hidden = !this.isActive;
    this.hideError();
    if (this.isActive) this.input.focus();
    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  onInput() {
    this.counter.textContent = `${this.input.value.length}/${this.input.maxLength}`;
    this.hideError();
    this.updatePlaque();
  }

  updatePlaque() {
    const text = this.text();
    this.plaque.textContent = text || this.emptyLabel;
    this.plaque.classList.toggle('is-empty', text === '');
  }

  showError() {
    this.error.hidden = false;
    this.input.setAttribute('aria-invalid', 'true');
    this.input.focus();
    this.input.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  hideError() {
    this.error.hidden = true;
    this.input.removeAttribute('aria-invalid');
  }
}

if (!customElements.get('cf-gravure')) {
  customElements.define('cf-gravure', CfGravure);
}
